import { createElementWithAttributes, isEmpty } from '@aiwyn/shared';
import { Injectable } from '@angular/core';
import { BehaviorSubject, defer, iif, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

export interface DidLoadScript {
  src: string;
  didLoad?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ScriptLoaderService {

  // List of scripts that are or have been loaded and their statuses
  private readonly _loadedScripts$: BehaviorSubject<DidLoadScript[]> = new BehaviorSubject<DidLoadScript[]>([]);

  constructor() {
  }

  /**
   * Loads a JS script into the SPA
   * @param scriptSrc the src of the script (if using local / relative path, specify it as an asset)
   * @param async whether the script should be loaded asynchronously
   * @param additionalAttributes additional attributes to attach to the script tag
   * @returns an observable that will report back if the script succeeded in loading or if it failed
   */
  loadScript(
    scriptSrc: string,
    async: boolean = false,
    additionalAttributes: { attribute: string, value: string }[] = [],
  ): Observable<boolean> {
    return this._loadedScripts$.pipe(
      switchMap(scripts => iif(
        // Check to see if the script has not been loaded or if it has loaded and failed
        () => {
          const runningScript: DidLoadScript = scripts.find(script => script.src === scriptSrc);
          return !runningScript || runningScript.didLoad === false;
        },
        // If it has not been run, or has failed, attempt to load the script
        defer(() => new Observable<boolean>((subscriber) => {
          const scriptTag = createElementWithAttributes('script', {
            src: scriptSrc,
            type: 'text/javascript',
            async,
          });

          // Append additional attributes to the script tag
          if (!isEmpty(additionalAttributes)) {
            additionalAttributes.forEach(additionalAttribute => scriptTag.setAttribute(additionalAttribute.attribute, additionalAttribute.value));
          }

          // onload is a successful script load
          scriptTag.onload = () => {
            subscriber.next(true);
            subscriber.complete();
            this._loadedScripts$.next([
              ...this._loadedScripts$.value.filter(scripts => scripts.src !== scriptSrc),
              { src: scriptSrc, didLoad: true }
            ]);
          };
          // onerror shows failure
          scriptTag.onerror = () => {
            console.warn('Failed to load JS Script');
            scriptTag.remove();
            subscriber.next(false);
            subscriber.complete();
            this._loadedScripts$.next([
              ...this._loadedScripts$.value.filter(scripts => scripts.src !== scriptSrc),
              { src: scriptSrc, didLoad: false }
            ]);
          };

          document.getElementsByTagName('head')[0].appendChild(scriptTag);
        })),
        // If it is processing or has successfully processed, return observable to status
        defer(() => this._loadedScripts$.pipe(
          // Find the script
          map(loadedScripts => loadedScripts.find(script => script.src === scriptSrc)),
          // Only emit if there is a known value for the didLoad
          filter(loadedScript => loadedScript && loadedScript.didLoad !== undefined),
          // toBoolean
          map(loadedScript => loadedScript.didLoad))
        )
      ))
    );
  }
}
